import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import QuizProgressTracker from './QuizProgressTracker';
import QuizResultDisplay from './QuizResultDisplay';
import MathText from '../../../../../common/MathText';
import { parseQuizDocxFromArrayBuffer } from '../../../../../../utils/quizDocxParser';
import {
    IMG_TAG_REGEX,
    containsHtmlTag,
    containsImageTag,
    containsMathContent,
    sanitizeDocxHtml,
    domNodeToReact as utilsDomNodeToReact,
    renderSanitizedHtml as utilsRenderSanitizedHtml,
    resetRichTextKeyCounter,
} from '../../../../../../utils/docxRichRender';

const QUESTIONS_PER_PAGE = 5;

const DocxRichText = React.memo(function DocxRichText({ text }) {
    const content = String(text || '');
    
    // DEBUG: Log câu hỏi gốc
    if (content.includes('ABCD')) {
        console.log('📝 Original text:', content);
    }

    if (!containsHtmlTag(content)) {
        // Co LaTeX trong noi dung text thuần thi parse bang MathText de tranh hien chuoi $...$ tho.
        if (containsMathContent(content)) {
            return <MathText text={content} />;
        }

        return <MathText text={content} />;
    }

    const safeHtml = sanitizeDocxHtml(content);
    // Bang (<table>) phai luon di qua renderer DOM-aware de khong bi vo cau truc
    // khi trong bang vua co cong thuc toan vua co anh (khong duoc split chuoi tho).
    const hasTableStructure = /<table\b/i.test(safeHtml);
    if (hasTableStructure) {
        resetRichTextKeyCounter();
        return utilsRenderSanitizedHtml(safeHtml);
    }
    // DEBUG: Log sau sanitize
    if (content.includes('ABCD')) {
        console.log('🛡️ After sanitize:', safeHtml);
        console.log('🔍 Contains Math:', containsMathContent(safeHtml));
        console.log('🖼️ Contains Image:', containsImageTag(safeHtml));
    }

    // Truong hop co ca LaTeX va <img>: tach anh ra de giu nguyen image, phan text van qua MathText.
    if (containsMathContent(safeHtml) && containsImageTag(safeHtml)) {
        const parts = safeHtml
            .split(IMG_TAG_REGEX)
            .filter((part) => String(part || '').trim() !== '');

        return (
            <>
                {parts.map((part, index) => {
                    if (containsImageTag(part)) {
                        return <span key={`img-${index}`} dangerouslySetInnerHTML={{ __html: part }} />;
                    }

                    if (containsMathContent(part)) {
                        return <MathText key={`math-${index}`} text={part} />;
                    }

                    return <span key={`html-${index}`} dangerouslySetInnerHTML={{ __html: part }} />;
                })}
            </>
        );
    }

    if (containsMathContent(safeHtml)) {
        if (content.includes('ABCD')) {
            console.log('🧮 Math HTML passed to MathText:', safeHtml);
        }
        return <MathText text={safeHtml} />;
    }

    return <span dangerouslySetInnerHTML={{ __html: safeHtml }} />;
});

/**
 * COMPONENT CHÍNH: QuizDocxPlaying
 * 
 * Mục đích: Hiển thị bài trắc nghiệm từ file DOCX
 * 
 * Tính năng:
 * 1. Đọc file DOCX chứa câu hỏi trắc nghiệm theo quy ước free-text
 * 2. Hỗ trợ 3 loại câu hỏi:
 *    - Single choice: Chọn 1 đáp án (A/B/C/D)
 *    - Multiple choice: Chọn nhiều đáp án (A,B,D)
 *    - Boolean: Đúng/Sai (2 đáp án)
 *    - Text input: Người dùng nhập câu trả lời tự do (ví dụ: điền vào chỗ trống)
 * 3. Render công thức toán học LaTeX trong câu hỏi/đáp án
 * 4. Hiển thị kết quả sau khi nộp bài
 * 5. Giải thích đáp án đúng
 * 
 * Props:
 * - docxUrl: Đường dẫn file DOCX chứa câu hỏi
 */
export default React.memo(function QuizDocxPlaying({ docxUrl }) {
    // ===== STATES =====
    const [questions, setQuestions] = useState([]); // Danh sách câu hỏi từ DOCX
    const [documentTitle, setDocumentTitle] = useState(''); // Tiêu đề/phần mở đầu của đề DOCX
    const [currentPage, setCurrentPage] = useState(0); // Trang câu hỏi đang hiển thị
    const [selectedAnswers, setSelectedAnswers] = useState({}); // Đáp án đã chọn: { questionId: answerIndex hoặc Set }
    const [showResults, setShowResults] = useState(false); // Có hiển thị kết quả không?
    const [score, setScore] = useState(0); // Số câu trả lời đúng
    const [loading, setLoading] = useState(true); // Đang load DOCX?
    const [error, setError] = useState(null); // Lỗi khi load DOCX
    const [quizStarted, setQuizStarted] = useState(false); // Đã bắt đầu làm bài chưa?
    const [durationMinutes, setDurationMinutes] = useState('35'); // Thời gian người dùng chọn (phút)
    const [timeLeft, setTimeLeft] = useState(0); // Thời gian còn lại (giây)
    const [timedOut, setTimedOut] = useState(false); // Bài bị nộp do hết giờ?
    const [timeConfigError, setTimeConfigError] = useState(''); // Lỗi nhập thời gian
    const [visibleHints, setVisibleHints] = useState({}); // Trang thai hien/an goi y theo tung cau
    // Mốc đầu của khối quiz để cuộn về đúng vị trí sau khi chuyển trang.
    const quizContainerRef = useRef(null);
    // Cờ này giúp effect biết lần đổi currentPage nào cần cuộn lên đầu.
    const shouldScrollToTopRef = useRef(false);

    /**
    * HÀM ĐỌC FILE DOCX
     * 
     * Luồng xử lý:
    * 1. Fetch file DOCX từ URL
     * 2. Đọc ArrayBuffer
    * 3. Parse DOCX raw text bằng mammoth
    * 4. Parse theo quy ước (Câu n, *A./*B., Đáp án mẫu)
    * 5. Chuẩn hóa thành format câu hỏi hiện tại
     * 6. Lưu vào state questions
     */
    const loadDocxQuiz = useCallback(async (url) => {
        try {
            setLoading(true);
            setError(null);
            
            // Fetch file DOCX
            const response = await fetch(url);
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            
            // Đọc dữ liệu binary
            const arrayBuffer = await response.arrayBuffer();
            if (arrayBuffer.byteLength === 0) {
                throw new Error('File DOCX rong');
            }

            // Parse DOCX thành danh sách câu hỏi theo quy ước free-text.
            const parsedResult = await parseQuizDocxFromArrayBuffer(arrayBuffer);
            const parsedQuestions = (parsedResult.questions || []).map((question) => ({
                ...question,
                question: question.questionHtml || question.question,
                sectionTitle: question.sectionTitleHtml || question.sectionTitle,
                sectionContent: question.sectionContentHtml || question.sectionContent,
                sharedContext: question.sharedContextHtml || question.sharedContextText || '',
                options: Array.isArray(question.optionsHtml) && question.optionsHtml.length > 0
                    ? question.optionsHtml
                    : question.options,
                explanation: question.explanationHtml || question.explanation,
                hint: question.hintHtml || question.hint,
            }));
            
            // DEBUG: Log các câu hỏi
            console.log('📚 Parsed questions:', parsedQuestions.map((q, i) => ({
                id: q.id,
                question: q.question,
                length: String(q.question || '').length
            })));

            if (parsedQuestions.length === 0) {
                throw new Error('Khong tim thay cau hoi hop le trong file DOCX');
            }
            
            setQuestions(parsedQuestions);
            setDocumentTitle(parsedResult.documentTitleHtml || parsedResult.documentTitle || '');
            setVisibleHints({});
            setLoading(false);
        } catch (err) {
            console.error('Error loading DOCX quiz:', err);
            setError(err.message || 'Khong the tai file cau hoi DOCX');
            setLoading(false);
        }
    }, []); // Empty dependencies vì không phụ thuộc state/props nào

    // Load DOCX khi component mount hoặc docxUrl thay đổi
    useEffect(() => {
        if (docxUrl) {
            loadDocxQuiz(docxUrl);
        }
    }, [docxUrl, loadDocxQuiz]);

    /**
     * HÀM XỬ LÝ CHỌN ĐÁP ÁN
     * 
     * Logic:
     * - Single/Boolean: Chọn 1 đáp án → thay thế đáp án cũ
     * - Multiple: Chọn nhiều → toggle (thêm/bỏ) đáp án vào Set
     * 
     * State selectedAnswers:
     * {
     *   1: 0,              // Câu 1: chọn đáp án A (index 0)
     *   2: Set([0, 2, 3]), // Câu 2: chọn A, C, D (multiple)
     *   3: 1               // Câu 3: chọn đáp án B (index 1)
     * }
     */
    const handleAnswerSelect = (questionId, answerIndex) => {
        const question = questions.find(q => q.id === questionId);
        if (!question) return;

        setSelectedAnswers(prev => {
            const newAnswers = { ...prev };

            // ✅ NẾU LÀ TEXT INPUT
            if (question.questionType === 'text') {
                // answerIndex ở đây chính là giá trị text user nhập
                newAnswers[questionId] = answerIndex; // Lưu trực tiếp string
                return newAnswers;
            }

            // Multiple choice (code cũ)
            if (question.questionType === 'multiple') {
                // Multiple choice: dùng Set để lưu nhiều đáp án
                const currentSet = newAnswers[questionId] || new Set();
                const newSet = new Set(currentSet);
                
                // Toggle: nếu đã chọn thì bỏ, chưa chọn thì thêm
                if (newSet.has(answerIndex)) {
                    newSet.delete(answerIndex);
                } else {
                    newSet.add(answerIndex);
                }
                
                newAnswers[questionId] = newSet;
            } else {
                // Single/Boolean choice: lưu trực tiếp index
                newAnswers[questionId] = answerIndex;
            }

            return newAnswers;
        });
    };

    // Chuan hoa 1 nghiem don le de gop cac cach viet tuong duong (x = 2, x=2, x=−2...).
    const normalizeAnswerToken = useCallback((value, caseSensitive = false) => {
        let token = String(value || '')
            .normalize('NFKC')
            .replace(/−/g, '-')
            .replace(/\s+/g, ' ')
            .trim();

        if (!caseSensitive) {
            token = token.toLowerCase();
        }

        token = token
            .replace(/\s*([=<>+\-*/^])\s*/g, '$1')
            .replace(/^[,;:.()[\]{}]+|[,;:.()[\]{}]+$/g, '');

        return token;
    }, []);

    // Tach cau tra loi thanh tap nghiem theo cac tu noi/ky tu ngan cach thong dung.
    const splitToAnswerSet = useCallback((raw, caseSensitive = false) => {
        const text = String(raw || '')
            .normalize('NFKC')
            .replace(/\bhoặc\b/gi, '|')
            .replace(/\bhoac\b/gi, '|')
            .replace(/\bor\b/gi, '|')
            .replace(/\bvà\b/gi, '|')
            .replace(/\bva\b/gi, '|')
            .replace(/[;,]/g, '|')
            .replace(/\s*\|\s*/g, '|')
            .trim();

        if (!text) {
            return new Set();
        }

        const parts = text
            .split('|')
            .map((part) => normalizeAnswerToken(part, caseSensitive))
            .filter(Boolean);

        return new Set(parts);
    }, [normalizeAnswerToken]);

    // So sanh 2 tap nghiem theo nguyen tac: dung du, khong thua, khong thieu.
    const areSetsEqual = useCallback((a, b) => {
        if (a.size !== b.size) {
            return false;
        }

        for (const value of a) {
            if (!b.has(value)) {
                return false;
            }
        }

        return true;
    }, []);

    /**
     * HÀM KIỂM TRA ĐÁP ÁN ĐÚNG
     * 
     * Logic:
     * - Multiple: So sánh Set đáp án đã chọn với Set đáp án đúng
     * - Single/Boolean: So sánh letter (A/B/C/D)
     * 
     * Return: true nếu đúng, false nếu sai
     */
    const isAnswerCorrect = useCallback((question, selectedAnswer) => {
        // ✅ KIỂM TRA TEXT INPUT
        if (question.questionType === 'text') {
            if (!selectedAnswer || typeof selectedAnswer !== 'string') return false;

            const userAnswer = selectedAnswer.trim();
            
            // Nếu user không nhập gì
            if (userAnswer === '') return false;

            const config = question.textInputConfig || {};

            // Normalize: chuyển về lowercase nếu không phân biệt hoa/thường
            const normalizedUserAnswer = config.caseSensitive 
                ? userAnswer 
                : userAnswer.toLowerCase();

            // ✅ KIỂM TRA XEM USER ANSWER CÓ KHỚP VỚI BẤT KỲ CORRECT ANSWER NÀO KHÔNG
            return question.correctAnswers.some(correctAns => {
                const normalizedCorrectAns = config.caseSensitive 
                    ? correctAns 
                    : correctAns.toLowerCase();

                // ✅ KHỚP CHÍNH XÁC
                if (config.exactMatch) {
                    return normalizedUserAnswer === normalizedCorrectAns;
                }

                // Che do mac dinh cho cau text: doi ca hai ve tap nghiem roi so sanh tap hop.
                const userSet = splitToAnswerSet(normalizedUserAnswer, config.caseSensitive);
                const correctSet = splitToAnswerSet(normalizedCorrectAns, config.caseSensitive);

                if (userSet.size === 0 || correctSet.size === 0) {
                    return false;
                }

                // So sánh theo tập hợp: đúng đủ, không thừa, không thiếu.
                return areSetsEqual(userSet, correctSet);
            });
        }

        // ✅ KIỂM TRA MULTIPLE CHOICE
        if (question.questionType === 'multiple') {
            if (!(selectedAnswer instanceof Set)) return false;
            
            const selectedLetters = Array.from(selectedAnswer)
                .map(idx => String.fromCharCode(65 + idx))
                .sort();
            
            const correctLetters = [...question.correctAnswers].sort();
            
            return JSON.stringify(selectedLetters) === JSON.stringify(correctLetters);
        }

        // ✅ KIỂM TRA SINGLE/BOOLEAN
        if (selectedAnswer === undefined) return false;
        
        const selectedLetter = String.fromCharCode(65 + selectedAnswer);
        return question.correctAnswers.includes(selectedLetter);
    }, [areSetsEqual, splitToAnswerSet]);

    const finishQuiz = useCallback((isTimedOut = false) => {
        const correctCount = questions.reduce((count, q) => {
            const selectedAnswer = selectedAnswers[q.id];
            return count + (isAnswerCorrect(q, selectedAnswer) ? 1 : 0);
        }, 0);

        setScore(correctCount);
        setTimedOut(isTimedOut);
        setShowResults(true);
    }, [questions, selectedAnswers, isAnswerCorrect]);

    // Dùng window.scrollTo thay vì scrollIntoView vì header đang sticky.
    const scrollToQuizTop = useCallback(() => {
        if (!quizContainerRef.current) {
            window.scrollTo({ top: 0, behavior: 'smooth' });
            return;
        }

        // Trừ nhẹ một khoảng để khối quiz không dính sát mép trên viewport.
        const topOffset = 12;
        const absoluteTop = quizContainerRef.current.getBoundingClientRect().top + window.scrollY - topOffset;

        window.scrollTo({
            top: Math.max(absoluteTop, 0),
            behavior: 'smooth'
        });
    }, []);

    /**
     * CÁC HÀM ĐIỀU HƯỚNG
     */
    const handleNextQuestion = () => {
        if (currentPage < totalPages - 1) {
            // Đánh dấu để effect cuộn sau khi React render xong trang câu hỏi mới.
            shouldScrollToTopRef.current = true;
            setCurrentPage(prev => prev + 1);
        }
    };

    const handlePreviousQuestion = () => {
        if (currentPage > 0) {
            // Giữ hành vi cuộn đồng bộ với nút sang trang tiếp theo.
            shouldScrollToTopRef.current = true;
            setCurrentPage(prev => prev - 1);
        }
    };

    /**
     * HÀM NỘP BÀI
     * 
     * Tính điểm: Đếm số câu trả lời đúng
     */
    const handleSubmitQuiz = () => {
        finishQuiz(false);
    };

    const handleStartQuiz = () => {
        const parsedMinutes = Number(durationMinutes);

        if (!Number.isFinite(parsedMinutes) || parsedMinutes <= 0) {
            setTimeConfigError('Vui lòng nhập thời gian lớn hơn 0 phút.');
            return;
        }

        setTimeConfigError('');
        setTimedOut(false);
        setTimeLeft(Math.floor(parsedMinutes * 60));
        setQuizStarted(true);
    };

    const toggleHint = useCallback((questionId) => {
        setVisibleHints((prev) => ({
            ...prev,
            [questionId]: !prev[questionId],
        }));
    }, []);

    /**
     * HÀM LÀM LẠI BÀI THI
     * 
     * Reset tất cả state về ban đầu
     */
    const handleRetakeQuiz = () => {
        setSelectedAnswers({});
        setCurrentPage(0);
        setShowResults(false);
        setScore(0);
        setTimedOut(false);
        setQuizStarted(false);
        setTimeLeft(0);
        setTimeConfigError('');
        setVisibleHints({});
    };

    /**
     * COMPUTED VALUES (useMemo để tối ưu)
     */

    const totalPages = useMemo(() => {
        if (questions.length === 0) return 0;
        return Math.ceil(questions.length / QUESTIONS_PER_PAGE);
    }, [questions.length]);

    const pageStartIndex = useMemo(() => {
        return currentPage * QUESTIONS_PER_PAGE;
    }, [currentPage]);

    const currentPageQuestions = useMemo(() => {
        return questions.slice(pageStartIndex, pageStartIndex + QUESTIONS_PER_PAGE);
    }, [questions, pageStartIndex]);

    const getSectionKey = useCallback((question) => {
        if (question.sectionId !== undefined && question.sectionId !== null) {
            return `section-${question.sectionId}`;
        }

        const title = String(question.sectionTitle || '').trim();
        const content = String(question.sectionContent || '').trim();
        if (!title && !content) {
            return '';
        }

        return `section-fallback-${title}::${content}`;
    }, []);

    const currentRangeEnd = useMemo(() => {
        return Math.min(pageStartIndex + currentPageQuestions.length, questions.length);
    }, [pageStartIndex, currentPageQuestions.length, questions.length]);

    // Tiến độ hoàn thành (%)
    const progress = useMemo(() => {
        if (questions.length === 0) return 0;
        return (currentRangeEnd / questions.length) * 100;
    }, [currentRangeEnd, questions.length]);

    const hasAnswerForQuestion = useCallback((question) => {
        if (question.questionType === 'multiple') {
            const selectedSet = selectedAnswers[question.id];
            return selectedSet instanceof Set && selectedSet.size > 0;
        }

        if (question.questionType === 'text') {
            const selectedText = selectedAnswers[question.id];
            return typeof selectedText === 'string' && selectedText.trim() !== '';
        }

        return selectedAnswers[question.id] !== undefined;
    }, [selectedAnswers]);

    const handleJumpToQuestion = useCallback((questionIndex) => {
        // Nhảy nhanh sang trang chứa câu được chọn rồi cuộn về đầu khối quiz.
        shouldScrollToTopRef.current = true;
        setCurrentPage(Math.floor(questionIndex / QUESTIONS_PER_PAGE));
    }, []);

    useEffect(() => {
        if (!shouldScrollToTopRef.current) {
            return;
        }

        // Chỉ cuộn sau khi currentPage đã cập nhật xong để tránh cuộn sai vị trí.
        shouldScrollToTopRef.current = false;
        scrollToQuizTop();
    }, [currentPage, scrollToQuizTop]);

    useEffect(() => {
        if (!quizStarted || showResults || timeLeft <= 0) {
            return undefined;
        }

        const timerId = setInterval(() => {
            setTimeLeft(prev => (prev > 0 ? prev - 1 : 0));
        }, 1000);

        return () => clearInterval(timerId);
    }, [quizStarted, showResults, timeLeft]);

    useEffect(() => {
        if (quizStarted && !showResults && timeLeft === 0) {
            finishQuiz(true);
        }
    }, [quizStarted, showResults, timeLeft, finishQuiz]);

    const isLastMinute = timeLeft > 0 && timeLeft <= 60;
    const paperBackgroundStyle = {
        // backgroundImage: "url('https://data.linklearn.vn/backgroundpagetest.png')",
        backgroundImage: "url('https://data.linklearn.vn/gi%E1%BA%A5y%20tr%E1%BA%AFng%20h%C6%A1i%20nh%C4%83n.png')", 
        // backgroundImage: "url('https://data.linklearn.vn/Lined%20paper.png')",
        // backgroundImage: "url('https://data.linklearn.vn/Wrinkled%20paper.png')",
        backgroundRepeat: 'repeat-y',
        backgroundSize: '100% auto',
        backgroundPosition: 'top center',
        borderRadius: '0.5rem',
        padding: '0.75rem'
    };

    const questionCardStyle = {
        backgroundColor: 'rgba(255, 255, 255, .5)',
        backdropFilter: 'blur(0.5px)'
    };

    /**
     * RENDER LOADING STATE
     */
    if (loading) {
        return (
            <div className="text-center p-4">
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Đang tải...</span>
                </div>
                <p className="mt-2">Đang tải câu hỏi...</p>
            </div>
        );
    }

    /**
     * RENDER ERROR STATE
     */
    if (error) {
        return (
            <div className="alert alert-danger" role="alert">
                <i className="fas fa-exclamation-triangle me-2"></i>
                <strong>Lỗi:</strong> {error}
                <div className="mt-2">
                    <small>
                        <strong>Gợi ý:</strong>
                        <ul className="mb-0 mt-1">
                            <li>Dam bao file DOCX duoc viet theo quy uoc: Cau n:</li>
                            <li>Dap an dung danh dau bang dau * o dau dong, vi du *A. Noi dung</li>
                            <li>Cau tu luan dung dong Dap an mau: dap an 1; dap an 2</li>
                            <li>De them goi y cho tung cau, dung dong: Goi y: ... hoac Suggest: ... (co the viet nhieu dong)</li>
                        </ul>
                    </small>
                </div>
            </div>
        );
    }

    /**
     * RENDER EMPTY STATE
     */
    if (questions.length === 0 || currentPageQuestions.length === 0) {
        return (
            <div className="alert alert-warning" role="alert">
                <i className="fas fa-info-circle me-2"></i>
                Khong tim thay cau hoi hop le trong file DOCX
            </div>
        );
    }

    /**
     * ===== RENDER MAIN UI =====
     */
    return (
        <div ref={quizContainerRef} className="card border-0">
            <div className="card-body body-quiz">
               

                {!showResults ? (
                    /* ===== MÀN HÌNH LÀM BÀI ===== */
                    <>
                        

                        <QuizProgressTracker
                            quizStarted={quizStarted}
                            isLastMinute={isLastMinute}
                            timeLeft={timeLeft}
                            durationMinutes={durationMinutes}
                            onDurationChange={setDurationMinutes}
                            onStartQuiz={handleStartQuiz}
                            timeConfigError={timeConfigError}
                            questions={questions}
                            hasAnswerForQuestion={hasAnswerForQuestion}
                            pageStartIndex={pageStartIndex}
                            currentRangeEnd={currentRangeEnd}
                            onJumpToQuestion={handleJumpToQuestion}
                            onRetakeQuiz={handleRetakeQuiz}
                            onSubmitQuiz={handleSubmitQuiz}
                            progress={progress}
                        />

                        {/* QUESTION: Hiển thị nhóm câu hỏi */}
                        <div className="d-flex flex-column gap-2 mb-4" 
                        style={paperBackgroundStyle}
                        >
                             {documentTitle && (
                                <div className="quiz-docx-title-box border rounded bg-white p-3 mb-3">
                                    <DocxRichText text={documentTitle} />
                                </div>
                            )}
                            {currentPageQuestions.map((question, pageIndex) => (
                                <div key={question.id} className="p-2 rounded" 
                                style={questionCardStyle}
                                // style={paperBackgroundStyle}
                                >
                                    {(() => {
                                        const questionIndex = pageStartIndex + pageIndex;
                                        const currentSectionKey = getSectionKey(question);
                                        const previousQuestion = questionIndex > 0 ? questions[questionIndex - 1] : null;
                                        const previousSectionKey = previousQuestion ? getSectionKey(previousQuestion) : '';
                                        const shouldShowSection = Boolean(currentSectionKey) && currentSectionKey !== previousSectionKey;

                                        if (!shouldShowSection) {
                                            return null;
                                        }

                                        return (
                                            <div className="border rounded bg-white p-2 mb-3">
                                                {!!question.sectionTitle && (
                                                    <div className="fw-bold mb-1">
                                                        <DocxRichText text={question.sectionTitle} />
                                                    </div>
                                                )}
                                                {!!question.sectionContent && (
                                                    <div className="small text-muted">
                                                        <DocxRichText text={question.sectionContent} />
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })()}
                                    <div className="d-flex align-items-start mb-0 gap-1 flex-wrap quiz-question-heading">
                                        {!!String(question.sharedContext || '').trim() && (
                                            <div className="w-100 border rounded bg-light p-2 mb-2">
                                                <DocxRichText text={question.sharedContext} />
                                            </div>
                                        )}
                                        <span className="q-label flex-shrink-0">Câu {question.id}.</span>
                                        <div className="quiz-question-text flex-grow-1" style={{ minWidth: 0 }}>
                                            <DocxRichText text={question.question} />
                                        </div>

                                        <div className="d-flex gap-1 ms-2 flex-shrink-0">
                                            {question.questionType === 'text' && (
                                                <span className="badge bg-secondary">
                                                    <i className="fas fa-keyboard me-1"></i>
                                                    Trả lời ngắn
                                                </span>
                                            )}
                                            {question.questionType === 'multiple' && (
                                                <span className="badge bg-warning text-dark">
                                                    <i className="fas fa-check-double me-1"></i>
                                                    Chọn nhiều đáp án
                                                </span>
                                            )}
                                            {question.questionType === 'boolean' && (
                                                <span className="badge bg-info">
                                                    <i className="fas fa-question-circle me-1"></i>
                                                    Đúng/Sai
                                                </span>
                                            )}
                                        </div>
                                    </div>

                                    {question.questionType === 'text' ? (
                                        <div className="mb-3">
                                            <input
                                                type="text"
                                                className="form-control form-control-lg quiz-text-input"
                                                placeholder={question.textInputConfig?.placeholder || 'Nhập câu trả lời...'}
                                                value={selectedAnswers[question.id] || ''}
                                                onChange={(e) => handleAnswerSelect(question.id, e.target.value)}
                                            />
                                            <small className="text-muted d-block mt-2">
                                                <i className="fas fa-info-circle me-1"></i>
                                                {question.textInputConfig?.caseSensitive
                                                    ? 'Phân biệt chữ hoa/thường'
                                                    : 'Không phân biệt chữ hoa/thường'}
                                                {' • '}
                                                {question.textInputConfig?.exactMatch
                                                    ? 'Khớp chính xác'
                                                    : 'Chứa từ khóa'}
                                            </small>
                                        </div>
                                    ) : (
                                        <div className="d-flex flex-column gap-1">
                                            {question.options.map((option, index) => {
                                                const letter = String.fromCharCode(65 + index);

                                                let isSelected = false;
                                                if (question.questionType === 'multiple') {
                                                    const selectedSet = selectedAnswers[question.id];
                                                    isSelected = selectedSet instanceof Set && selectedSet.has(index);
                                                } else {
                                                    isSelected = selectedAnswers[question.id] === index;
                                                }

                                                return (
                                                    <div
                                                        key={index}
                                                        className={`quiz-option-card d-flex align-items-start rounded ${
                                                            isSelected
                                                                ? 'bg-primary opt'
                                                                : 'bg-answers '
                                                        }`}
                                                        onClick={() => handleAnswerSelect(question.id, index)}
                                                    >
                                                        <div className="d-flex align-items-center w-100 quiz-option-content">
                                                            <div
                                                                className={`me-3 d-flex align-items-center justify-content-center ${
                                                                    question.questionType === 'multiple'
                                                                        ? 'rounded'
                                                                        : 'rounded-circle'
                                                                } ${
                                                                    isSelected ? 'bg-white text-primary' : 'bg-light'
                                                                } quiz-option-badge ${
                                                                    question.questionType === 'multiple' && isSelected
                                                                        ? 'quiz-option-badge-multiple-selected'
                                                                        : ''
                                                                }`}
                                                            >
                                                                {question.questionType === 'multiple' && isSelected ? (
                                                                    <i className="fas fa-check"></i>
                                                                ) : (
                                                                    letter
                                                                )}
                                                            </div>
                                                            <span className="quiz-option-text">
                                                                <DocxRichText text={option} />
                                                            </span>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}

                                    {question.questionType === 'multiple' && (
                                        <div className="alert alert-warning mt-3 mb-0" role="alert">
                                            <small>
                                                <i className="fas fa-info-circle me-1"></i>
                                                <strong>Lưu ý:</strong> Câu hỏi này có nhiều đáp án đúng. Chọn TẤT CẢ đáp án đúng để được điểm.
                                            </small>
                                        </div>
                                    )}

                                    {!!String(question.hint || '').trim() && (
                                        <div className="mt-3">
                                            <button
                                                type="button"
                                                className="btn btn-sm btn-outline-warning"
                                                onClick={() => toggleHint(question.id)}
                                            >
                                                <i className="fas fa-lightbulb me-1"></i>
                                                {visibleHints[question.id] ? 'Ẩn gợi ý' : 'Xem gợi ý'}
                                            </button>

                                            {visibleHints[question.id] && (
                                                <div className="alert alert-warning mt-2 mb-0" role="alert">
                                                    <strong className="d-block mb-1">Gợi ý</strong>
                                                    <DocxRichText text={question.hint} />
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>

                        {/* NAVIGATION: Nút điều hướng */}
                        <div className="d-flex justify-content-between mb-4">
                            <button 
                                className="btn btn-outline-secondary"
                                onClick={handlePreviousQuestion}
                                disabled={currentPage === 0}
                            >
                                <i className="fas fa-arrow-left me-2"></i>
                                5 câu trước
                            </button>

                            {/* Nút "Nộp bài" ở trang cuối, "5 câu tiếp" ở các trang khác */}
                            {currentPage === totalPages - 1 ? (
                                <button 
                                    className="btn btn-success"
                                    onClick={handleSubmitQuiz}
                                >
                                    <i className="fas fa-check me-2"></i>
                                    Nộp bài
                                </button>
                            ) : (
                                <button 
                                    className="btn btn-primary"
                                    onClick={handleNextQuestion}
                                >
                                    5 câu tiếp
                                    <i className="fas fa-arrow-right ms-2"></i>
                                </button>
                            )}
                        </div>

                        
                    </>
                ) : (
                    /* ===== MÀN HÌNH KẾT QUẢ ===== */
                    <QuizResultDisplay
                        score={score}
                        questions={questions}
                        timedOut={timedOut}
                        selectedAnswers={selectedAnswers}
                        isAnswerCorrect={isAnswerCorrect}
                        onRetakeQuiz={handleRetakeQuiz}
                        MathTextComponent={DocxRichText}
                    />
                )}
            </div>
        </div>
    );
});